// EventTransactionListModal.jsx
import React, { useMemo, useState } from "react";
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonIcon,
    IonFooter
} from "@ionic/react";
import {arrowBack, closeCircleOutline, closeOutline, copyOutline, downloadOutline, receiptOutline} from "ionicons/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function statusColor(payment_status) {
    if (!payment_status) return "medium";
    const s = String(payment_status).toLowerCase();
    if (["paid", "captured", "success"].some(k => s.includes(k))) return "success";
    if (["pending", "created", "processing"].some(k => s.includes(k))) return "warning";
    if (["failed", "refunded", "cancel"].some(k => s.includes(k))) return "danger";
    return "medium";
}

function fmtAmount(v) {
    if (v === null || v === undefined || v === "") return "-";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
    try {
        return new Date(d).toLocaleString();
    } catch {
        return d || "-";
    }
}

function copy(text) {
    if (!text) return;
    navigator.clipboard?.writeText(String(text));
}

export default function EventTransactionListModal({
                                                 isOpen,
                                                 onDidDismiss,
                                                 title = "Transactions",
                                                 transactions = []
                                             }) {
    const [q, setQ] = useState("");

    // Helper: safe lowercasing + trimming
    const norm = (v) =>
        (v ?? "")
            .toString()
            .toLowerCase()
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "") // strip diacritics
            .trim();

    // Build a single searchable string for each row
    const rowSearchText = (t) => {
        const fields = [
            t.name,
            t.email,
            t.mobile_no,
            t.entity_name,
            t.gstin,
            t.event_ids,               // can be array or CSV/string
            t.razorpay_payment_id,
            t.registration_id,
            t.payment_status,
            t.total_price              // let amount be searchable too
        ];
        return norm(fields.join(" "));
    };

    // AND-match all tokens (so “rahul paid” narrows down)
    const filterTransactions = (list, query) => {
        const qn = norm(query);
        if (!qn) return list;

        const tokens = qn.split(/\s+/).filter(Boolean);
        return list.filter((t) => {
            const hay = rowSearchText(t);
            return tokens.every((tok) => hay.includes(tok));
        });
    };

    // Example usage: recompute when data or query changes
    const filtered = React.useMemo(
        () => filterTransactions(transactions, q),
        [transactions, q]
    );


    const downloadExcel = () => {
        if (!transactions?.length) return;

        const rows = transactions.map((t) => ({
            Name: t.name || "-",
            Email: t.email || "-",
            "Mobile No": t.mobile_no || "-",
            Entity: t.entity_name || "-",
            GSTIN: t.gstin || "-",
            "Event IDs": Array.isArray(t.event_ids) ? t.event_ids.join(", ") : (t.event_ids || "-"),
            "Payment ID": t.razorpay_payment_id || "-",
            "Reg ID": t.registration_id || "-",
            Status: t.payment_status || "-",
            Amount: fmtAmount(t.total_price),
            Created: fmtDate(t.created_at)
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "transactions.xlsx");
    };

    return (
        <IonModal  isOpen={isOpen}
                   className="sheet_modal_header event_transaction_modal"
                   onDidDismiss={onDidDismiss}
                   breakpoints={[0, 1]}
                   initialBreakpoint={1}>
            <IonHeader className="job_detail_sheet_header sheet_modal_header">
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton className={"close_job_post_button"} onClick={onDidDismiss}>
                            <IonIcon icon={arrowBack}/>
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{title}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={downloadExcel} disabled={!transactions.length}>
                            <IonIcon icon={downloadOutline} slot="icon-only" />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>

                <IonToolbar>
                    {/* Header + search */}
                    <div className="filter_search_row">
                        <div className="fs_left">
                            <IonIcon icon={receiptOutline} className="fs_icon" />
                            <h3 className="fs_title">Transactions</h3>
                            <span className="fs_count">
                              <strong>{filtered.length}</strong> result{filtered.length === 1 ? "" : "s"}
                            </span>
                        </div>

                        <div className="fs_right">
                            <input
                                type="search"
                                className="fs_input"
                                value={q}
                                placeholder="Search name, email, mobile, entity, GSTIN, payment ID, reg ID…"
                                onChange={(e) => setQ(e.target.value || "")}
                                aria-label="Search transactions"
                            />
                            {q && (
                                <button
                                    type="button"
                                    className="fs_clear_btn"
                                    onClick={() => setQ("")}
                                    aria-label="Clear search"
                                    title="Clear"
                                >
                                    <IonIcon icon={closeCircleOutline} />
                                </button>
                            )}
                        </div>
                    </div>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                <div className="event_transaction_main_container_card">
                    {filtered.map((t) => (
                        <div
                            className="tr_card"
                            key={`${t.registration_id}-${t.razorpay_payment_id || Math.random()}`}
                        >
                            {/* CARD HEADER */}
                            <div className="tr_card_header">
                                <div className="left">
                                    <div className="payer_name"><strong>{t.name || "-"}</strong></div>
                                    <div className="payer_email">{t.email || "-"}</div>
                                </div>
                                <div className="right">
                                    <div className="amount">₹ {fmtAmount(t.total_price)}</div>
                                    <span className="badge" data-color={statusColor(t.payment_status)}>
            {t.payment_status || "—"}
          </span>
                                </div>
                            </div>

                            {/* CARD BODY */}
                            <div className="tr_card_body">
                                {/* Billing */}
                                <div className="field">
                                    <div className="label">Entity</div>
                                    <div className="value"><strong>{t.entity_name || "-"}</strong></div>
                                </div>

                                <div className="field">
                                    <div className="label">GSTIN</div>
                                    <div className="value">{t.gstin || "-"}</div>
                                </div>

                                {/* Payment meta */}
                                <div className="field">
                                    <div className="label">Payment ID</div>
                                    <div className="value mono">{t.razorpay_payment_id || "-"}</div>
                                </div>

                                <div className="field">
                                    <div className="label">Reg ID</div>
                                    <div className="value mono">{t.registration_id || "-"}</div>
                                </div>

                                <div className="field">
                                    <div className="label">Created</div>
                                    <div className="value note">{fmtDate(t.created_at)}</div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {filtered.length === 0 && (
                        <div className="empty_state">
                            <IonIcon className="empty_icon" icon={copyOutline} />
                            <p>No transactions found.</p>
                        </div>
                    )}
                </div>
            </IonContent>

            <IonFooter className="ion-padding-horizontal blank_footer_bar ion-padding-bottom">
                <div className="helper">
                    Tip: use the search box to filter by <em>name, email, payment id, GSTIN, status</em>, etc.
                </div>
            </IonFooter>
        </IonModal>
    );
}
